/**
 * GatedSendManager — extracted from the App class.
 *
 * Owns every recruiter-facing dispatch that may be **gated** by a
 * `PendingAction` (consent, phone-verify, upgrade, confirm-questions,
 * confirm-slots…). Public methods uniformly return `GatedResult<TRes>`:
 *
 *   - `action !== null` → blocker fired; caller surfaces the matching
 *     UI gate and re-invokes once resolved.
 *   - `action === null` → dispatch succeeded; `res` carries the per-
 *     candidate batch results when applicable.
 *
 * Cache patching, event emission and error→action mapping all live here
 * so `App` stays a thin delegator.
 */
import { AppEventType } from './appEvents';
import { ApiError, CandidateState, HiringStatus, advanceStageForCapability, StageCapability, ErrorCodes, MessageTransport, nowUtc, } from '@common/clientServerApi';
import { SubscriptionTier } from '@common/subscriptionTypes';
export class GatedSendManager {
    constructor(deps) {
        this.deps = deps;
    }
    get api() { return this.deps.getApi(); }
    get cache() { return this.deps.dataCache; }
    get events() { return this.deps.eventEmitter; }
    // ===============================
    // Public gated dispatch
    // ===============================
    /**
     * Gated SMS send. Single user-facing operation regardless of recipient
     * count — the caller passes a list, this method decides routing
     * (single vs batch endpoint) and gating (cache-driven preflight or
     * server-issued action). Server-side blockers (`ConsentRequired`,
     * credit exhaustion) are mapped to the same `PendingAction` shape, so
     * callers never need to distinguish "client cache said no" from
     * "server said no".
     */
    async sendSms(candidateIds, jobId, transport = MessageTransport.Sms) {
        console.log(`[GatedSendManager] sendSms: jobId=${jobId} count=${candidateIds.length} ids=${JSON.stringify(candidateIds)}`);
        // 1. Cache-driven preflight — short-circuit before any network call.
        //    UI-only gate: job script must be confirmed by the recruiter
        //    before we send. Surfaced as a PendingAction so the UI handler
        //    treats it the same as consent/verify/upgrade.
        const job = this.cache.getJobs().find(j => j.id === jobId);
        if (job && !job.questionsConfirmed) {
            console.log('[GatedSendManager] sendSms: gated on confirm_questions (job.questionsConfirmed=false)');
            return { action: { type: 'confirm_questions' } };
        }
        const local = this.preflightSendSms(candidateIds);
        if (local) {
            console.log(`[GatedSendManager] sendSms: gated locally action=${local.type}`);
            return { action: local };
        }
        // 2. Dispatch. Single-candidate uses the per-candidate endpoint
        //    (which returns an explicit `action_required` envelope); bulk
        //    uses the batch endpoint (which throws on gate violations).
        try {
            if (candidateIds.length === 1) {
                const res = await this.deps.candidateManager.sendMessage(candidateIds[0], jobId, transport);
                console.log(`[GatedSendManager] sendSms: single-candidate response status=${res.status}`);
                return res.status === 'action_required'
                    ? { action: res.action }
                    : { action: null, res: undefined };
            }
            await this.batchSendMessages(candidateIds, jobId, transport);
            console.log('[GatedSendManager] sendSms: batch dispatched successfully');
            return { action: null, res: undefined };
        }
        catch (e) {
            console.error('[GatedSendManager] sendSms dispatch error:', e);
            const action = this.mapSendErrorToAction(e);
            if (action)
                return { action };
            throw e;
        }
    }
    /**
     * **Propose availability** — start booking negotiation by SMSing the
     * candidate(s) a list of recruiter-chosen slots so they can pick one.
     * No calendar event is created until the candidate selects a slot.
     *
     * Gated identically to `sendSms` (Free-tier preflight + server-issued
     * `action_required` + thrown ApiError → `PendingAction`).
     */
    async proposeAvailability(candidateIds, jobId, args) {
        if (args.slots.length === 0) {
            throw new Error('proposeAvailability requires at least one slot');
        }
        const local = this.preflightSendSms(candidateIds);
        if (local)
            return { action: local };
        try {
            const res = await this.batchInviteToInterview(candidateIds, jobId, args.saveSlots, args.slots, /* scheduleDirectly */ false, args.touchpointOverrides, /* reminders */ undefined);
            return { action: null, res };
        }
        catch (e) {
            const action = this.mapSendErrorToAction(e);
            if (action)
                return { action };
            throw e;
        }
    }
    /**
     * **Schedule interview directly** — the recruiter has already agreed a
     * time with the candidate (e.g. by phone). Books the interview event
     * immediately on a single concrete slot and dispatches a confirmation
     * SMS to the candidate.
     */
    async scheduleInterview(candidateId, jobId, args) {
        const local = this.preflightSendSms([candidateId]);
        if (local)
            return { action: local };
        try {
            const res = await this.batchInviteToInterview([candidateId], jobId, 
            /* saveSlots */ undefined, [args.slot], /* scheduleDirectly */ true, 
            /* touchpointOverrides */ undefined, args.reminders);
            return { action: null, res };
        }
        catch (e) {
            const action = this.mapSendErrorToAction(e);
            if (action)
                return { action };
            throw e;
        }
    }
    /**
     * Grant per-candidate SMS consent for one or more candidates. The
     * recruiter affirms — once per candidate — that they have permission
     * to text them. The server stamps `consentApproved={approved:true,…}`
     * on each doc; we patch the cache locally and emit per-candidate
     * updates so any open SendSmsModal / ActionGate refreshes immediately.
     */
    async grantCandidateConsent(candidateIds) {
        if (candidateIds.length === 0)
            return;
        const res = await this.deps.makeApiCallWithRetry(() => this.api.grantCandidateConsent({ candidateIds }));
        for (const updated of res.candidates) {
            this.cache.updateCandidate(updated);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
        }
    }
    // ===============================
    // Lower-level batch endpoints (also called by the methods above)
    // ===============================
    /** Batch send SMS screening messages to multiple candidates in a single request. */
    async batchSendMessages(candidateIds, jobId, transport = MessageTransport.Sms) {
        const res = await this.deps.makeApiCallWithRetry(() => this.api.batchSendMessages({ candidateIds, jobId, transport }));
        // Patch local cache for successful sends
        const job = this.cache.getJob(jobId);
        for (const r of res.results) {
            if (r.success && r.candidateState) {
                const cached = this.cache.getCandidates().find(c => c.id === r.candidateId);
                const prior = cached?.applications?.[jobId];
                if (cached && prior && job) {
                    // Replace (not mutate) the application so identity-keyed memos invalidate.
                    const nextApp = {
                        ...prior,
                        state: r.candidateState,
                        stageId: advanceStageForCapability(prior.stageId, StageCapability.SmsScreening, job.pipelineTemplate),
                    };
                    if (r.qualificationQuestions) {
                        nextApp.qualificationQuestions = r.qualificationQuestions;
                    }
                    const updated = { ...cached, applications: { ...cached.applications, [jobId]: nextApp } };
                    this.cache.updateCandidate(updated);
                    this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                }
            }
        }
        return res;
    }
    /** Batch invite multiple candidates to interview in a single request. */
    async batchInviteToInterview(candidateIds, jobId, saveSlots, slots, scheduleDirectly, touchpointOverrides, reminders) {
        const res = await this.deps.makeApiCallWithRetry(() => this.api.batchInviteToInterview({
            candidateIds, jobId, saveSlots, slots, scheduleDirectly, touchpointOverrides, reminders,
        }));
        // Patch local cache for successful invites
        const job = this.cache.getJob(jobId);
        for (const r of res.results) {
            if (r.success) {
                const cached = this.cache.getCandidates().find(c => c.id === r.candidateId);
                const prior = cached?.applications?.[jobId];
                if (cached && prior && job) {
                    const nextState = scheduleDirectly
                        ? CandidateState.InterviewScheduled
                        : CandidateState.InterviewSchedulingSent;
                    const reinvited = prior.hiringStatus === HiringStatus.InterviewAttended;
                    // Replace (not mutate) the application so identity-keyed memos invalidate.
                    const nextApp = {
                        ...prior,
                        state: nextState,
                        // Mirror the server: advance to the interview stage for the candidate's
                        // current position (a repeated round moves to the next interview stage).
                        stageId: advanceStageForCapability(prior.stageId, StageCapability.Interview, job.pipelineTemplate),
                        // Mirror the server: a fresh interview round clears a prior round's
                        // attendance so the row shows "awaiting slot", not a stale "Checked in".
                        ...(reinvited ? { hiringStatus: HiringStatus.InterviewInvited } : {}),
                    };
                    // When the recruiter sent a preset snapshot, patch the cache
                    // immediately so the candidate drawer's Reminders tab shows
                    // the same list without an extra round-trip.
                    if (scheduleDirectly && reminders) {
                        nextApp.reminders = reminders;
                    }
                    if (r.proposedTimeSlots?.length) {
                        // Rebuild the invitation from scratch (matching interviewService) so a
                        // re-invite drops the prior round's confirmed slot + status.
                        nextApp.interviewInvitation = {
                            sent: true,
                            sentTimeUtc: nowUtc(),
                            timeSlots: r.proposedTimeSlots,
                            confirmed: scheduleDirectly,
                            declined: false,
                            canceled: false,
                            status: scheduleDirectly ? 'confirmed' : 'sent',
                            lastResponseStatus: 'pending',
                            ...(scheduleDirectly ? { selectedSlot: r.proposedTimeSlots[0] } : {}),
                        };
                    }
                    const updated = { ...cached, applications: { ...cached.applications, [jobId]: nextApp } };
                    this.cache.updateCandidate(updated);
                    this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                }
            }
        }
        return res;
    }
    // ===============================
    // Internal: gate computation
    // ===============================
    /**
     * Local mirror of the server's pre-send gate. Inspects the cache and
     * returns the same `PendingAction` envelope the server would emit if it
     * rejected the call — so callers handle one shape regardless of where
     * the gate fired. Returns `null` if the recruiter may proceed.
     *
     * The server is still authoritative; this just avoids round-trips when
     * the answer is knowable client-side (e.g. Free-tier bulk-send is
     * structurally impossible — no need to ask the server).
     */
    preflightSendSms(candidateIds) {
        const org = this.cache.getOrganization();
        if (org?.subscription.tier !== SubscriptionTier.FREE)
            return null;
        if (candidateIds.length > 1) {
            return {
                type: 'upgrade_plan',
                reason: 'feature_required',
                message: 'Bulk SMS sending requires a paid plan. Upgrade to Starter to send to multiple candidates at once.',
            };
        }
        return null;
    }
    /**
     * Map a thrown send-pipeline error to a `PendingAction` for the UI.
     * Shared by every gated dispatch in this manager — the gates fire
     * identically because the transport (SMS) is the same. Returns `null`
     * for unrecognised errors so callers can rethrow.
     */
    mapSendErrorToAction(e) {
        if (!ApiError.isApiError(e))
            return null;
        if (e.code === ErrorCodes.SMS_CONSENT_REQUIRED) {
            return { type: 'request_consent' };
        }
        if (e.code === ErrorCodes.PHONE_VERIFICATION_REQUIRED) {
            return { type: 'verify_phone' };
        }
        if (e.code === ErrorCodes.INSUFFICIENT_CREDITS) {
            return { type: 'upgrade_plan', reason: 'credits_exhausted', message: e.message };
        }
        if (e.code === ErrorCodes.BULK_SEND_REQUIRES_UPGRADE) {
            return { type: 'upgrade_plan', reason: 'feature_required', message: e.message };
        }
        return null;
    }
}
